import { useCallback, useEffect, useState } from "react";
import { ApiKey, ApiKeySchema } from "../models/apiKeys";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";

export const useApiKeys = ({
  fetchOnLoad = true,
}: {
  fetchOnLoad?: boolean;
}) => {
  const [loading, setLoading] = useState(fetchOnLoad);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFastApi({ path: "/api_keys/", method: "GET" });
      if (data.api_keys) {
        const parsedApiKeys = ApiKeySchema.array().parse(data.api_keys);
        setApiKeys(parsedApiKeys);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch API keys");
    } finally {
      setLoading(false);
    }
  }, []);

  const createApiKey = useCallback(
    async (name: string) => {
      try {
        await fetchFastApi({
          path: "/api_keys/create",
          method: "POST",
          body: { name },
        });
        fetchApiKeys();
      } catch (error) {
        console.error(error);
        toast.error("Failed to create API key");
      }
    },
    [fetchApiKeys],
  );

  const deleteApiKey = useCallback(
    async (id: string) => {
      try {
        await fetchFastApi({ path: `/api_keys/${id}`, method: "DELETE" });
        fetchApiKeys();
        toast.success("API key deleted");
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete API key");
      }
    },
    [fetchApiKeys],
  );

  useEffect(() => {
    if (fetchOnLoad) {
      fetchApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOnLoad]);

  return { loading, apiKeys, fetchApiKeys, createApiKey, deleteApiKey };
};
